// External dependencies

// Internal dependencies
import { transformCheckBalanceApiResponse } from "@payment-client/helpers/WalletUtils";
import * as WalletApi from "@payment-client/interfaces/WalletApi";
import {
    AddMoneyResponseData,
    APaySignAndEncryptParams,
    LinkWalletParams,
    WalletLinkExtraParams,
} from "@payment-client/interfaces/WalletPublic";
import { APIRequestBody, UnlinkAPIResponse } from "@payment-client/interfaces/CorporateApi";

// Local imports

import { makeDownstreamApiCall } from "@swiggy-private/checkout-core";
import { APAY, CORPORATE_WALLET_DELINK_URI } from "@payment-client/constants/WalletV3Endpoints";
import { unwrapResponse, unwrapResponseWithCode, unwrapCorporateResponse } from "@payment-client/utils/Api";
import { ApiResponse } from "@payment-client/interfaces/Api";
import {
    checkWalletBalance,
    linkWallet as linkWalletService,
    addMoneyToWallet,
    validateLinkWallet,
    delinkWallet,
} from "@payment-client/apis/publicWalletService";
import { DOWNSTREAM_HOSTS } from "@payment-client/constants/Hosts";

export async function getWalletBalancePublic(
    apiHost: string,
    walletCode: string,
): Promise<WalletApi.GetWalletBalanceResponse> {
    const walletCodeFinal = walletCode.toLowerCase();
    const response = await checkWalletBalance(apiHost, walletCodeFinal);
    return transformCheckBalanceApiResponse(walletCodeFinal, response);
}

export async function linkWalletPublic(
    apiHost: string,
    walletCode: string,
    mobileNumber?: string,
    params?: WalletLinkExtraParams,
): Promise<object | null> {
    const walletCodeFinal = walletCode.toLowerCase();
    const response = await linkWalletService(apiHost, walletCodeFinal, mobileNumber, params);

    if (response.statusCode === 0) {
        return (response.data || {}) as object;
    }

    throw response;
}

// TODO: to figure out the secret key approach and consume it later for the public flow
export async function addMoneyPublic(
    apiHost: string,
    params: {
        amount: string | number;
        cell: string | number;
        addMoneyReturnUrl?: string;
        isPaymentModeFlow?: boolean;
        walletCode: string;
    },
): Promise<AddMoneyResponseData> {
    const normalizedParams = {
        ...params,
        walletCode: params.walletCode.toLowerCase(),
    };
    const response = await addMoneyToWallet(apiHost, normalizedParams);

    return unwrapResponse(response).toPromise();
}

export async function validateLinkWalletV3(
    apiHost: string,
    params: LinkWalletParams,
): Promise<WalletApi.VerifyOtpResponse> {
    const normalizedParams = {
        ...params,
        walletCode: params.walletCode.toLowerCase(),
    };
    const response = await validateLinkWallet(apiHost, normalizedParams);
    return unwrapResponseWithCode(response).toPromise();
}

export async function delinkWalletV3(apiHost: string, walletCode: string): Promise<AddMoneyResponseData> {
    const walletCodeFinal = walletCode.toLowerCase();
    const response = await delinkWallet(apiHost, walletCodeFinal);
    return unwrapResponse(response).toPromise();
}

export async function aPaySignAndEncryptPublic(
    apiHost: string,
    params: APaySignAndEncryptParams,
): Promise<WalletApi.APaySignAndEncrypt> {
    const response = (await makeDownstreamApiCall<WalletApi.APaySignAndEncrypt>({
        apiHost,
        endpoint: APAY.SIGN_AND_ENCRYPT,
        bodyParams: { ...params },
        options: {
            method: "POST",
        },
    })) as ApiResponse<WalletApi.APaySignAndEncrypt>;

    return unwrapResponse(response).toPromise();
}

export async function delinkCorporateWallet(reqBody: APIRequestBody): Promise<UnlinkAPIResponse> {
    const response = (await makeDownstreamApiCall<UnlinkAPIResponse>({
        apiHost: DOWNSTREAM_HOSTS.PARTNER_REWARDS_HOST,
        endpoint: CORPORATE_WALLET_DELINK_URI,
        bodyParams: { ...reqBody },
        options: {
            method: "POST",
        },
    })) as ApiResponse<UnlinkAPIResponse>;

    return unwrapCorporateResponse(response).toPromise();
}
